import { FBPage } from 'components/FbUI/Page/FBPage'
import {
  FBPageHeader,
  FBPageInnerTabsContainer,
  FBPageTableContainer,
  FBPageTitle,
  FBPageTitleRow,
} from 'components/FbUI/Page/styles'
import { Tabs } from 'components/FbUI/Tabs'
import { UnderlineTabs } from 'components/FbUI/UnderlineTabs'
import { RebateDashboard } from 'features/tradespend/RebateDashboard'
import { RebateOffersTable } from 'features/tradespend/RebateOffersTable'
import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useNavbarStore } from 'components/Layout/navbar-store'
import { CreateRebateOfferModal } from 'features/tradespend/CreateRebateOfferModal'
import { RebateClaimsTable } from 'features/tradespend/RebateClaimsTable'
import { RebatePaymentsTab } from 'features/tradespend/RebatePaymentsTab'
import { RebateCurrentInvoiceTab } from 'features/tradespend/RebateCurrentInvoiceTab'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { Modal } from 'components/UI/Modal/Modal'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import { ToolbarDensityProvider } from 'components/Tables/CommonTableCells/ToolbarDensityContext'
import { PaginatedRebateOffers } from 'features/tradespend/tradespend_api'
import { PiSealPercent } from 'react-icons/pi'
import { Spinner } from 'react-bootstrap'

const TABLE_CONTAINER_HEIGHT = 'calc(var(--base-page-table-height) - 95px)'

export function Rebates() {
  const navigate = useNavigate()
  const location = useLocation()
  const setLastRebateVisited = useNavbarStore((s) => s.setLastRebateVisited)
  const selectedTab = location.pathname.split('/')[2] || 'dashboard'

  useEffect(() => {
    // Only redirect if we're exactly at /rebates without a tab
    // Use replace to avoid infinite history entries
    if (location.pathname === '/rebates' || location.pathname === '/rebates/') {
      const url = '/rebates/offers'
      navigate(url, { replace: true })
      setLastRebateVisited(url)
    }
  }, [location.pathname, navigate, setLastRebateVisited])

  const tabs = [
    { key: 'dashboard' as const, title: 'Dashboard' },
    { key: 'offers' as const, title: 'Offers' },
    { key: 'claims' as const, title: 'Claims' },
    { key: 'payments' as const, title: 'Invoices' },
    { key: 'preview-invoice' as const, title: 'Preview Invoice' },
  ]
  type RebatesTab = (typeof tabs)[number]['key']

  const { isLoading: isFeatureFlagsLoading, enableRebates } =
    useFeatureFlagService()

  if (!isFeatureFlagsLoading && !enableRebates) {
    return <ModalWall />
  }

  return (
    <FBPage
      innerPageClassName="!bg-surface-page !p-[8px]"
      containerClassName="!bg-surface-page !shadow-none !px-[16px] !py-[12px]"
    >
      <FBPageHeader>
        <FBPageTitleRow>
          {/* Eyebrow ("Rebates" + left-nav icon) over a tab-driven title,
              matching the Contacts / Campaigns header pattern. */}
          <div className="flex flex-col">
            <span className="mb-2 inline-flex items-center gap-1.5 self-start rounded-full bg-neutral-100 px-2.5 py-0.5 text-sm font-medium tracking-tight text-neutral-700">
              <PiSealPercent size={14} />
              Rebates
            </span>
            <FBPageTitle className="!text-fg">
              {/* "Dashboard" alone is ambiguous (the app has several
                  dashboards), so qualify it; other tabs are already specific. */}
              {selectedTab === 'dashboard'
                ? 'Rebates Dashboard'
                : (tabs.find((t) => t.key === selectedTab)?.title ?? 'Rebates')}
            </FBPageTitle>
          </div>
          {/* Inside FBPageTitleRow (justify-between, full content width) so its
              right edge lands on the content edge — i.e. flush with the right
              end of the underline-tabs divider below. */}
          <div className="flex shrink-0 gap-3">
            <CreateRebateOfferModal />
          </div>
        </FBPageTitleRow>
      </FBPageHeader>

      <FBPageInnerTabsContainer>
        <UnderlineTabs<RebatesTab>
          tabs={tabs}
          activeKey={selectedTab as RebatesTab}
          onChange={(value) => {
            const url = `/rebates/${value}`
            navigate(url)
            setLastRebateVisited(url)
          }}
          idPrefix="rebates"
          aria-label="Rebates views"
        />
      </FBPageInnerTabsContainer>

      {isFeatureFlagsLoading ? (
        <div className="flex items-center justify-center h-full">
          <Spinner />
        </div>
      ) : (
        <>
          {selectedTab === 'dashboard' && (
            <div className="flex-1 overflow-y-auto">
              <RebateDashboard />
            </div>
          )}
          {selectedTab === 'offers' && (
            <TableLinkStyleProvider variant="plain">
              <FBPageTableContainer
                className="flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white"
                style={{
                  height: TABLE_CONTAINER_HEIGHT,
                  boxShadow:
                    '0px 1px 3px 0px rgba(16, 24, 40, 0.08), 0px 1px 2px -1px rgba(16, 24, 40, 0.04)',
                }}
              >
                <ToolbarDensityProvider value="sm">
                  <RebateOffersTable />
                </ToolbarDensityProvider>
              </FBPageTableContainer>
            </TableLinkStyleProvider>
          )}
          {selectedTab === 'claims' && (
            <TableLinkStyleProvider variant="plain">
              <FBPageTableContainer
                className="flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white"
                style={{
                  height: TABLE_CONTAINER_HEIGHT,
                  boxShadow:
                    '0px 1px 3px 0px rgba(16, 24, 40, 0.08), 0px 1px 2px -1px rgba(16, 24, 40, 0.04)',
                }}
              >
                <ToolbarDensityProvider value="sm">
                  <RebateClaimsTable />
                </ToolbarDensityProvider>
              </FBPageTableContainer>
            </TableLinkStyleProvider>
          )}
          {selectedTab === 'payments' && (
            <TableLinkStyleProvider variant="plain">
              <FBPageTableContainer
                className="flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white"
                style={{
                  height: TABLE_CONTAINER_HEIGHT,
                  boxShadow:
                    '0px 1px 3px 0px rgba(16, 24, 40, 0.08), 0px 1px 2px -1px rgba(16, 24, 40, 0.04)',
                }}
              >
                <ToolbarDensityProvider value="sm">
                  <RebatePaymentsTab />
                </ToolbarDensityProvider>
              </FBPageTableContainer>
            </TableLinkStyleProvider>
          )}
          {selectedTab === 'preview-invoice' && (
            <FBPageTableContainer style={{ height: TABLE_CONTAINER_HEIGHT }}>
              <RebateCurrentInvoiceTab />
            </FBPageTableContainer>
          )}
        </>
      )}
    </FBPage>
  )
}

function ModalWall() {
  const tabs = [
    { key: 'dashboard', title: 'Dashboard' },
    { key: 'offers', title: 'Offers' },
    { key: 'claims', title: 'Claims' },
    { key: 'payments', title: 'Invoices' },
    { key: 'preview-invoice', title: 'Preview Invoice' },
  ]

  const columnsOrder = [
    'name',
    'learn_more_url',
    'state',
    'scope',
    'offer_type',
    'valid_from',
    'valid_to',
    'total_enrollments',
    'total_claims',
    'pending_validation_claims',
    'invalid_claims',
    'paid_claims',
    'unpaid_valid_claims',
    'total_validated_units',
    'total_validated_payout',
    'created',
    'modified',
  ]

  const mockData: PaginatedRebateOffers = {
    count: 20,
    next: null,
    previous: null,
    results: [
      {
        id: 1,
        name: 'Impossible Chicken Sandiwch', // Typo from image
        description: null,
        learn_more_url: '#',
        code: 'impossible-chicken-sandwich',
        state: 'DELETED',
        scope: 'PUBLIC',
        offer_type: 'FIRST_OFFER',
        valid_from: '2025-04-24',
        valid_to: '2025-05-14',
        deleted_at: new Date().toISOString(), // Set deleted_at for DELETED state
        products: [],
        terms: { product_name: '', unit: 'unit', tiers: [], products: [] },
        product_names: '',
        total_enrollments: 0,
        total_claims: 0,
        pending_validation_claims: 0,
        invalid_claims: 0,
        unpaid_valid_claims: 0,
        paid_claims: 0,
        total_validated_units: null,
        total_validated_payout: null,
        include_deleted: false,
        is_shared_with_network: true,
        created: '2025-04-20T10:00:00Z', // Example date
        modified: '2025-04-24T11:00:00Z', // Example date
      },
      {
        id: 2,
        name: 'AC Foods - New Customer Offer',
        description: null,
        learn_more_url: '#',
        code: 'ac-foods-new-customer',
        state: 'PUBLISHED',
        scope: 'PUBLIC',
        offer_type: 'FIRST_OFFER',
        valid_from: '2025-04-21',
        valid_to: '2025-05-11',
        deleted_at: null,
        products: [],
        terms: { product_name: '', unit: 'unit', tiers: [], products: [] },
        product_names: '',
        total_enrollments: 1,
        total_claims: 1,
        pending_validation_claims: 0,
        invalid_claims: 0,
        unpaid_valid_claims: 0,
        paid_claims: 0,
        total_validated_units: null,
        total_validated_payout: null,
        include_deleted: false,
        is_shared_with_network: false,
        created: '2025-04-15T10:00:00Z',
        modified: '2025-04-21T11:00:00Z',
      },
      {
        id: 3,
        name: 'Pineapple Mango Smoothie',
        description: null,
        learn_more_url: '#',
        code: 'pineapple-mango-smoothie',
        state: 'PUBLISHED',
        scope: 'PUBLIC',
        offer_type: 'FIRST_OFFER',
        valid_from: '2025-04-09',
        valid_to: '2025-04-29',
        deleted_at: null,
        products: [],
        terms: { product_name: '', unit: 'unit', tiers: [], products: [] },
        product_names: '',
        total_enrollments: 1,
        total_claims: 1,
        pending_validation_claims: 1,
        invalid_claims: 0,
        unpaid_valid_claims: 0,
        paid_claims: 0,
        total_validated_units: null,
        total_validated_payout: null,
        include_deleted: false,
        is_shared_with_network: true,
        created: '2025-04-01T10:00:00Z',
        modified: '2025-04-09T11:00:00Z',
      },
      {
        id: 4,
        name: 'Spring Trial Promotion',
        description: null,
        learn_more_url: '#',
        code: 'spring-trial-promo',
        state: 'PUBLISHED',
        scope: 'PUBLIC',
        offer_type: 'FIRST_OFFER',
        valid_from: '2025-03-28',
        valid_to: '2025-04-17',
        deleted_at: null,
        products: [],
        terms: { product_name: '', unit: 'unit', tiers: [], products: [] },
        product_names: '',
        total_enrollments: 2,
        total_claims: 1,
        pending_validation_claims: 1,
        invalid_claims: 0,
        unpaid_valid_claims: 0,
        paid_claims: 0,
        total_validated_units: null,
        total_validated_payout: null,
        include_deleted: false,
        is_shared_with_network: false,
        created: '2025-03-20T10:00:00Z',
        modified: '2025-03-28T11:00:00Z',
      },
      {
        id: 5,
        name: 'Spring Promo',
        description: null,
        learn_more_url: '#',
        code: 'spring-promo',
        state: 'PUBLISHED',
        scope: 'PUBLIC',
        offer_type: 'FIRST_OFFER',
        valid_from: '2024-12-31',
        valid_to: '2025-06-30',
        deleted_at: null,
        products: [],
        terms: { product_name: '', unit: 'unit', tiers: [], products: [] },
        product_names: '',
        total_enrollments: 0,
        total_claims: 0,
        pending_validation_claims: 0,
        invalid_claims: 0,
        unpaid_valid_claims: 0,
        paid_claims: 0,
        total_validated_units: null,
        total_validated_payout: null,
        include_deleted: false,
        is_shared_with_network: true,
        created: '2024-12-20T10:00:00Z',
        modified: '2024-12-31T11:00:00Z',
      },
      {
        id: 6,
        name: 'Eduardo Test',
        description: null,
        learn_more_url: '#',
        code: 'eduardo-test',
        state: 'DELETED',
        scope: 'PUBLIC',
        offer_type: 'FIRST_OFFER',
        valid_from: '2025-03-25',
        valid_to: '2025-04-14',
        deleted_at: new Date().toISOString(),
        products: [],
        terms: { product_name: '', unit: 'unit', tiers: [], products: [] },
        product_names: '',
        total_enrollments: 0,
        total_claims: 0,
        pending_validation_claims: 0,
        invalid_claims: 0,
        unpaid_valid_claims: 0,
        paid_claims: 0,
        total_validated_units: null,
        total_validated_payout: null,
        include_deleted: false,
        is_shared_with_network: false,
        created: '2025-03-15T10:00:00Z',
        modified: '2025-03-25T11:00:00Z',
      },
      {
        id: 7,
        name: 'Eduardo Test Offer',
        description: null,
        learn_more_url: '#',
        code: 'eduardo-test-offer',
        state: 'DELETED',
        scope: 'PUBLIC',
        offer_type: 'FIRST_OFFER',
        valid_from: '2025-03-25',
        valid_to: '2025-04-14',
        deleted_at: new Date().toISOString(),
        products: [],
        terms: { product_name: '', unit: 'unit', tiers: [], products: [] },
        product_names: '',
        total_enrollments: 1,
        total_claims: 1,
        pending_validation_claims: 1,
        invalid_claims: 0,
        unpaid_valid_claims: 0,
        paid_claims: 0,
        total_validated_units: null,
        total_validated_payout: null,
        include_deleted: false,
        is_shared_with_network: true,
        created: '2025-03-15T10:00:00Z',
        modified: '2025-03-25T11:00:00Z',
      },
      {
        id: 8,
        name: 'Aperture Science Lemonade',
        description: null,
        learn_more_url: '#',
        code: 'aperture-science-lemonade',
        state: 'PUBLISHED',
        scope: 'PUBLIC',
        offer_type: 'FIRST_OFFER',
        valid_from: '2025-03-25',
        valid_to: '2025-04-14',
        deleted_at: null,
        products: [],
        terms: { product_name: '', unit: 'unit', tiers: [], products: [] },
        product_names: '',
        total_enrollments: 0,
        total_claims: 0,
        pending_validation_claims: 0,
        invalid_claims: 0,
        unpaid_valid_claims: 0,
        paid_claims: 0,
        total_validated_units: null,
        total_validated_payout: null,
        include_deleted: false,
        is_shared_with_network: false,
        created: '2025-03-15T10:00:00Z',
        modified: '2025-03-25T11:00:00Z',
      },
      {
        id: 9,
        name: "V's Afterlife Cocktail",
        description: null,
        learn_more_url: '#',
        code: 'afterlife-cocktail',
        state: 'PUBLISHED',
        scope: 'PUBLIC',
        offer_type: 'FIRST_OFFER',
        valid_from: '2025-03-25',
        valid_to: '2025-04-14',
        deleted_at: null,
        products: [],
        terms: { product_name: '', unit: 'unit', tiers: [], products: [] },
        product_names: '',
        total_enrollments: 0,
        total_claims: 0,
        pending_validation_claims: 0,
        invalid_claims: 0,
        unpaid_valid_claims: 0,
        paid_claims: 0,
        total_validated_units: null,
        total_validated_payout: null,
        include_deleted: false,
        is_shared_with_network: true,
        created: '2025-03-15T10:00:00Z',
        modified: '2025-03-25T11:00:00Z',
      },
    ],
  }

  const onAccept = () => {
    // Open link in new tab
    const url =
      'https://calendly.com/matt-firstbite/first-bite-rebates-rewards-intro'
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer')
    if (newWindow) {
      newWindow.opener = null
    }
  }

  return (
    <div className="relative flex-1 overflow-hidden">
      <Modal
        scopeBackdrop
        blurLevel={2}
        open={true}
        title={
          <span className="font-normal">
            <b>Rebates:</b> Learn More
          </span>
        }
        onAccept={onAccept}
        acceptButtonText={'Schedule Meeting'}
        size="md"
        hideCancelButton
        disableCloseIcon
        childrenContainerClassName="max-h-[70vh] overflow-x-hidden max-w-full w-full my-3"
      >
        <span className="font-normal text-gray-600">
          <p className="mb-2">
            First Bite now lets you generate, process, and track operator
            rebates.
          </p>
          <p>
            Create targeted offers for high-priority accounts to help drive
            sales and gather deep data on your foodservice customer base.
            Variable payment processing fees apply.
          </p>
        </span>
      </Modal>
      <FBPage>
        <FBPageHeader>
          <FBPageTitleRow>
            <FBPageTitle>Rebates</FBPageTitle>
          </FBPageTitleRow>
          <div className="flex gap-3 mx-6">
            <CreateRebateOfferModal />
          </div>
        </FBPageHeader>
        {/* Put the mock.png image here to fake data */}
        <FBPageInnerTabsContainer>
          <Tabs
            tabs={tabs}
            selectedTab={tabs[1].key}
            setSelectedTab={() => {}}
            tabStyle={{ width: '125px' }}
          />
          <FBPageTableContainer style={{ height: TABLE_CONTAINER_HEIGHT }}>
            <RebateOffersTable
              mockData={mockData}
              defaultColumnVisibility={{
                description: false,
              }}
              initialColumnOrder={columnsOrder}
            />
          </FBPageTableContainer>
        </FBPageInnerTabsContainer>
      </FBPage>
    </div>
  )
}
